import { renderToString } from "react-dom/server";
import React from "react";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import Highcharts from "highcharts";
import { merge } from "lodash";
import { BankHealthFilterIntervalEnum } from "@rentablo/api/apiv3";
import { PerformanceValuationTooltip } from "@components/Tooltip/PerformanceValuationTooltip";
import { GainLossChartPercents } from "@components/Tooltip/GainLossChartPercents";
import { toPercent } from "@helpers";
import { useCommonHighchartsOptions } from "../../../../hooks/chart/useCommonHighchartsOptions";

export const useChartOptions = (
  chartData: [number, number][],
  mode: "success" | "failure",
  interval: BankHealthFilterIntervalEnum
): Highcharts.Options => {
  const { t } = useTranslation();
  const commonHighchartsOptions = useCommonHighchartsOptions();
  const theme = useTheme();

  const lineColor =
    mode === "success"
      ? theme.palette.graphVisual["02"].mid
      : theme.palette.graphVisual["04"].mid;
  const name =
    mode === "success"
      ? t("admin.bankHealth.table.successRate")
      : t("admin.bankHealth.table.failureRate");

  return merge({}, commonHighchartsOptions, {
    chart: {
      type: "area",
    },
    series: [
      {
        name,
        data: chartData,
        type: "area",
        color: lineColor,
      },
    ],
    title: {
      text: "",
    },
    tooltip: {
      split: true,
      formatter: function () {
        return PerformanceValuationTooltip({
          dateFormatter: (value) =>
            `${format(new Date(value!), "dd.MM.yyyy")}${
              interval === BankHealthFilterIntervalEnum.QUARTER ||
              interval === BankHealthFilterIntervalEnum.HOUR
                ? ` um ${format(new Date(value!), "HH:mm")}`
                : ""
            }`,
          formatter: (value) => (
            <GainLossChartPercents
              theme={theme}
              value={(value as number) / 100}
              reverseColor={mode === "failure"}
            />
          ),
          theme,
          points: this.points,
          x: this.x,
        });
      },
    },
    xAxis: {
      type: "datetime",
      tickLength: 0,
      lineColor: theme.palette.border.secondary,
      crosshair: {
        color: theme.palette.border.secondary,
      },
      labels: {
        showLastLabel: true,
        showFirstLabel: true,
        startOnTick: true,
        endOnTick: true,
        formatter: function (
          data: Highcharts.AxisLabelsFormatterContextObject
        ) {
          return renderToString(
            <div
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.caption.fontSize,
                color: theme.palette.text.secondary,
                textAlign: "left",
              }}
            >
              {format(new Date(data.value), "dd.MM.yyyy")}
            </div>
          );
        },
      },
    },
    yAxis: {
      title: {
        text: "",
      },
      max: 100,
      min: 0,
      tickAmount: 6,
      labels: {
        formatter: (data) => {
          return renderToString(
            <div
              style={{
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.caption.fontSize,
                fontWeight: 600,
                color: theme.palette.text.secondary,
              }}
            >
              {toPercent((data.value as number) / 100, "auto", 0, 0)}
            </div>
          );
        },
      },
    },
    plotOptions: {
      area: {
        fillColor: {
          stops: [
            [
              0,
              Highcharts.color(lineColor).setOpacity(0.1).get("rgba") as string,
            ],
            [
              1,
              Highcharts.color(lineColor).setOpacity(0).get("rgba") as string,
            ],
          ],
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
  } as Highcharts.Options);
};
